import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";

import { parseDate } from "../../../helpers/date";
import { useDirect837Upload } from "../../../helpers/ediDirectUpload";
import {
  IEdi837Ingest,
  useDelete837IngestMutation,
  useGetAll837IngestsQuery,
  useReprocess837IngestMutation,
} from "../../../store/queries/ediConversion";
import { useGetAllProvidersQuery } from "../../../store/queries/providers";
import {
  EdiClaimType,
  IEdiUploadMapping,
} from "../../../types/ediConversionFile";
import { IProvider, SpecialtyList } from "../../../types/Provider/Provider";

// Same claim-type → provider mapping vocabulary as the 835 upload (kept minimal here — the 837 store
// only needs provider attribution, not the 835's per-payer taxonomy/pricing filters).
const CLAIM_TYPE_OPTIONS: { value: EdiClaimType; label: string }[] = [
  { value: "INSTITUTIONAL", label: "Institutional" },
  { value: "PROFESSIONAL_ANESTHESIA", label: "Professional — Anesthesia" },
  { value: "PROFESSIONAL_PROVIDER", label: "Professional — Provider" },
];

const allowedSpecialtiesForClaimType = (
  claimType: EdiClaimType
): SpecialtyList[] => {
  if (claimType === "INSTITUTIONAL") return [SpecialtyList.FACILITY];
  if (claimType === "PROFESSIONAL_ANESTHESIA")
    return [SpecialtyList.FACILITY, SpecialtyList.ANESTHESIOLOGIST];
  return [
    SpecialtyList.FACILITY,
    SpecialtyList.PATHOLOGIST,
    SpecialtyList.PRIMARY_PROVIDER,
    SpecialtyList.ASSISTANT_PROVIDER,
  ];
};

const newMapping = (claimType: EdiClaimType): IEdiUploadMapping => ({
  claimType,
  providerId: "",
  placeOfServiceFilter: [],
});

const statusSeverity = (s: IEdi837Ingest["status"]) => {
  if (s === "COMMITTED") return "success";
  if (s === "REJECTED") return "danger";
  return "info";
};

/**
 * CLE-2483 — the "837 Submissions" tab: standalone 837 uploads (decoupled from any 835). Lists
 * Edi837FileIngest rows with submission + match counts, and an "Upload 837" dialog that mirrors the
 * 835 upload's provider-mapping selection.
 */
export default function Edi837IngestsTable() {
  const toast = useRef<Toast>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Debounce so we don't fire a request per keystroke (searches name, file name, and provider).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const { data, isLoading, refetch } = useGetAll837IngestsQuery(
    debouncedSearch ? { search: debouncedSearch } : undefined
  );
  const { data: providers } = useGetAllProvidersQuery();
  const [reprocess837] = useReprocess837IngestMutation();
  const [delete837] = useDelete837IngestMutation();
  const { upload } = useDirect837Upload();

  const ingests = data?.data ?? [];

  // Poll while any ingest is still PARSING.
  useEffect(() => {
    if (!ingests.some((i) => i.status === "PARSING")) return undefined;
    const t = setInterval(() => refetch(), 4000);
    return () => clearInterval(t);
  }, [ingests, refetch]);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [mappings, setMappings] = useState<IEdiUploadMapping[]>([
    newMapping("INSTITUTIONAL"),
  ]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);

  const resetForm = () => {
    setDisplayName("");
    setFile(null);
    setMappings([newMapping("INSTITUTIONAL")]);
    setProgress(null);
  };

  const updateMapping = (idx: number, patch: Partial<IEdiUploadMapping>) =>
    setMappings((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, ...patch } : m))
    );
  const addMapping = () => {
    const used = mappings.map((m) => m.claimType);
    const next = CLAIM_TYPE_OPTIONS.map((o) => o.value).find(
      (t) => t === "PROFESSIONAL_PROVIDER" || !used.includes(t)
    );
    setMappings((prev) => [
      ...prev,
      newMapping(next ?? "PROFESSIONAL_PROVIDER"),
    ]);
  };
  const removeMapping = (idx: number) =>
    setMappings((prev) => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    const completeMappings = mappings.filter((m) => m.providerId);
    if (!displayName.trim() || !file || completeMappings.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail:
          "Provide a display name, choose one 837 file, and select at least one provider mapping",
      });
      return;
    }
    setIsUploading(true);
    setProgress(0);
    try {
      await upload({
        file,
        displayName: displayName.trim(),
        mappings: completeMappings,
        onProgress: (loaded, total) =>
          setProgress(total > 0 ? (loaded / total) * 100 : 0),
      });
      toast.current?.show({
        severity: "info",
        summary: "837 upload complete — ingestion started",
        detail:
          "The 837 is being parsed into the store and matched against existing claims.",
      });
      resetForm();
      setDialogVisible(false);
      refetch();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail:
          error?.data?.message || error?.message || "Failed to upload 837",
        life: 10000,
      });
    } finally {
      setIsUploading(false);
      setProgress(null);
    }
  };

  const onReprocess = async (id: string) => {
    try {
      await reprocess837(id).unwrap();
      toast.current?.show({
        severity: "info",
        summary: "Reprocess started",
        detail: "Re-ingesting the 837.",
      });
      refetch();
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: e?.data?.message || "Reprocess failed",
      });
    }
  };

  const onDelete = (row: IEdi837Ingest) =>
    confirmDialog({
      message: `Delete "${
        row.displayName ?? row.fileName ?? "this 837 ingest"
      }" and its ${
        row.submissionCount
      } submission(s)? Matched claims will be re-reconciled.`,
      header: "Delete 837 ingest",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await delete837(row.id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Deleted",
            detail: "837 ingest deleted.",
          });
          refetch();
        } catch (e: any) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: e?.data?.message || "Delete failed",
          });
        }
      },
    });

  // Column body renderers as named consts (not inline arrows) — matches the 835 table's pattern and
  // satisfies react/no-unstable-nested-components.
  const statusBody = (r: IEdi837Ingest) => (
    <Tag value={r.status} severity={statusSeverity(r.status)} />
  );
  const matchedBody = (r: IEdi837Ingest) =>
    r.submissionCount > 0
      ? `${r.matchedCount} / ${r.submissionCount} (${Math.round(
          (r.matchedCount / r.submissionCount) * 100
        )}%)`
      : "—";
  const actionsBody = (r: IEdi837Ingest) => (
    <div style={{ display: "flex", gap: "0.25rem" }}>
      <Button
        icon="pi pi-refresh"
        className="p-button-text p-button-sm"
        tooltip="Reprocess"
        disabled={r.status === "PARSING"}
        onClick={() => onReprocess(r.id)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        tooltip="Delete"
        onClick={() => onDelete(r)}
      />
    </div>
  );

  if (isLoading) return <div>Loading…</div>;

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="flex justify-content-between align-items-center mb-2">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search provider, name, file..."
            className="w-20rem"
          />
        </span>
        <Button
          label="Upload 837"
          icon="pi pi-upload"
          onClick={() => setDialogVisible(true)}
        />
      </div>
      <div className="text-sm text-muted mb-3">
        Standalone 837 uploads — parsed into the submission store and matched
        against 835 claims across files.
      </div>

      <DataTable
        value={ingests}
        paginator
        rows={10}
        emptyMessage="No 837 uploads yet."
      >
        <Column
          field="providerName"
          header="Provider"
          body={(r: IEdi837Ingest) => r.providerName ?? "—"}
        />
        <Column
          field="displayName"
          header="Name"
          body={(r: IEdi837Ingest) => r.displayName ?? "—"}
        />
        <Column
          field="fileName"
          header="File"
          body={(r: IEdi837Ingest) => r.fileName ?? "—"}
        />
        <Column header="Status" body={statusBody} />
        <Column
          header="Submissions"
          body={(r: IEdi837Ingest) => r.submissionCount}
        />
        <Column header="Matched" body={matchedBody} />
        <Column
          header="Uploaded"
          body={(r: IEdi837Ingest) => parseDate(r.createdAt)}
        />
        <Column header="Actions" body={actionsBody} />
      </DataTable>

      <Dialog
        header="Upload 837"
        visible={dialogVisible}
        style={{ width: "560px" }}
        onHide={() => !isUploading && setDialogVisible(false)}
      >
        <div className="mb-3">
          <label className="block font-semibold mb-1">Display Name *</label>
          <InputText
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full"
            disabled={isUploading}
            placeholder="e.g. Heritage 837 — 2026-06"
          />
        </div>

        <div className="mb-3">
          <label className="block font-semibold mb-1">837 File *</label>
          {file && (
            <div className="text-sm mb-1">
              {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </div>
          )}
          <FileUpload
            mode="basic"
            name="file837"
            accept=".txt,.edi,.837,.zip"
            maxFileSize={200000000}
            chooseLabel={file ? "Change file" : "Choose 837 File"}
            className="w-full"
            onSelect={(e) => setFile((e.files?.[0] as File) ?? null)}
            disabled={isUploading}
          />
        </div>

        <div className="mb-3">
          <label className="block font-semibold mb-1">
            Provider mapping(s) *
          </label>
          {mappings.map((m, idx) => {
            const allowed = allowedSpecialtiesForClaimType(m.claimType);
            const providerOpts = (providers?.data ?? [])
              .filter((p: IProvider) => allowed.includes(p.specialty))
              .map((p: IProvider) => ({ label: p.name, value: p.id }));
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div
                key={idx}
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                <Dropdown
                  value={m.claimType}
                  options={CLAIM_TYPE_OPTIONS}
                  onChange={(e) =>
                    updateMapping(idx, {
                      ...newMapping(e.value as EdiClaimType),
                    })
                  }
                  className="w-full"
                  disabled={isUploading}
                  appendTo="self"
                />
                <Dropdown
                  value={m.providerId}
                  options={providerOpts}
                  onChange={(e) => updateMapping(idx, { providerId: e.value })}
                  placeholder="Select provider"
                  className="w-full"
                  filter
                  showClear
                  disabled={isUploading}
                  appendTo="self"
                />
                {mappings.length > 1 && (
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-danger"
                    type="button"
                    disabled={isUploading}
                    aria-label="Remove mapping"
                    onClick={() => removeMapping(idx)}
                  />
                )}
              </div>
            );
          })}
          {mappings.length < 4 && (
            <Button
              label="Add mapping"
              icon="pi pi-plus"
              className="p-button-text p-button-sm"
              onClick={addMapping}
              disabled={isUploading}
            />
          )}
        </div>

        {progress !== null && (
          <div className="mb-2">
            <div className="text-sm mb-1">
              Uploading… {Math.round(progress)}%
            </div>
            <ProgressBar value={progress} />
          </div>
        )}

        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => setDialogVisible(false)}
            disabled={isUploading}
          />
          <Button
            label="Upload"
            icon="pi pi-upload"
            onClick={handleUpload}
            loading={isUploading}
          />
        </div>
      </Dialog>
    </div>
  );
}
